import CONFIG from '../../config';

type Locale = 'ru' | 'az';

/**
 * Extracts locale from URL pathname
 * Checks for locale prefix in path (e.g., /ru/... or /az/...)
 * Falls back to hostname detection if no prefix found
 *
 * @param pathname - URL pathname (e.g., '/ru/search' or '/search')
 * @param hostname - Optional hostname for fallback detection (e.g., 'ru.bina.az')
 * @returns Language code ('ru' or 'az')
 */
export const getLocaleFromUrl = (
  pathname: string,
  hostname?: string
): Locale => {
  // Extract first segment after / (e.g., 'ru' from '/ru/search')
  const pathPrefix = pathname.split('/')[1];

  if (pathPrefix && CONFIG.languages.includes(pathPrefix)) {
    return pathPrefix as Locale;
  }

  if (hostname) {
    const normalizedHostname = hostname.toLowerCase();
    return normalizedHostname.startsWith('ru.')
      ? 'ru'
      : (CONFIG.defaultLocale as Locale);
  }

  return CONFIG.defaultLocale as Locale;
};

/**
 * Gets locale from browser URL (client-side only)
 * Uses window.location.pathname and hostname
 * Note: For client-side navigation, prefer using router.asPath if available
 *
 * @param routerAsPath - Optional router.asPath (more reliable for client-side navigation)
 * @returns Language code ('ru' or 'az')
 */
export const getLocaleFromBrowser = (routerAsPath?: string): Locale => {
  if (typeof window === 'undefined') {
    return CONFIG.defaultLocale as Locale;
  }

  // Prefer router.asPath if provided (more reliable for client-side navigation)
  // router.asPath includes locale prefix even during client-side navigation
  if (routerAsPath) {
    return getLocaleFromUrl(routerAsPath);
  }

  const { pathname, hostname } = window.location;
  return getLocaleFromUrl(pathname, hostname);
};
